/**
 * NewDemandSplitButton — "+ New demand", with its two forms (owner, 2026-10-06).
 *
 * - Full form: the intake dialog; you pick the focus area and every field.
 * - Simple form: the two-field submission; the app places it and it arrives
 *   in the audit queue like anyone else's request.
 *
 * The primary click opens the full form for council and the simple form for
 * everyone else. The dropdown offers both.
 */

import {
  type ButtonProps,
  Menu,
  MenuButtonProps,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SplitButton,
} from '@fluentui/react-components'
import { Add20Regular } from '@fluentui/react-icons'
import { useNavigate } from 'react-router-dom'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { bayerColors } from '../theme'

export function NewDemandSplitButton({ onIntake }: { onIntake: () => void }) {
  const navigate = useNavigate()
  const isCouncil = useEffectiveUser()?.role === 'council'
  const openSimple = () => navigate('/submit-demand')
  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        {(triggerProps: MenuButtonProps) => (
          <SplitButton
            appearance="primary"
            icon={<Add20Regular />}
            menuButton={triggerProps}
            primaryActionButton={
              {
                onClick: isCouncil ? onIntake : openSimple,
                // The testid the plain button carried — callers' tests keep working.
                'data-testid': 'new-demand-button',
                // Fluent's slot typing doesn't admit data-* attributes, though the
                // runtime forwards them fine — hence the cast.
              } as unknown as ButtonProps
            }
            style={{ background: bayerColors.navyMid, borderColor: bayerColors.navyMid }}
          >
            New demand
          </SplitButton>
        )}
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem data-testid="new-demand-as-council" onClick={onIntake}>
            Full form
          </MenuItem>
          <MenuItem data-testid="new-demand-as-requestor" onClick={openSimple}>
            Simple form
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  )
}
